import type { FilterCondition } from '@/lib/crm/filters/types';

/**
 * Derive the pipeline association type name from its stage association name
 * by stripping the trailing `_stage` suffix (e.g.
 * `ahoy_deal_to_deal_pipeline_stage` -> `ahoy_deal_to_deal_pipeline`).
 * Shared by the board (PipelinePage.tsx) and the table filter below so the
 * derivation only needs to change in one place.
 */
export function getPipelineAssociationTypeName(
  stageAssociationTypeName: string
): string {
  return stageAssociationTypeName.replace(/_stage$/, '');
}

/**
 * Build the removable pipeline-scope filter chip conditions for the CRM
 * table when it is opened from a pipeline route (`/pipeline/:slug/:pipelineId`
 * with `?view=table`). Reproduces the board's own pipeline association
 * filter (see PipelinePage.tsx pipelineFilterGroups): the association is
 * derived from the stage association by stripping the trailing `_stage`,
 * and the value is the route pipeline entity id.
 *
 * Returns [] when the entity is not pipelineable (no stageAssociationTypeName)
 * or there is no pipelineId — so a non-pipelineable entity mounted on the
 * generic route, or a plain /crm/:slug route, seeds no chip.
 */
export function buildPipelineScopeFilterConditions(
  stageAssociationTypeName: string | undefined,
  pipelineId: string | undefined
): FilterCondition[] {
  if (!stageAssociationTypeName || !pipelineId) {
    return [];
  }
  const pipelineAssociationTypeName = getPipelineAssociationTypeName(
    stageAssociationTypeName
  );
  return [
    {
      id: 'pipeline-scope',
      field: pipelineAssociationTypeName,
      // Frontend OperatorType `'equals'` → backend `'exact'` via
      // convertConditionGroupsToFilterGroups when useListState wraps this
      // into a single group at apply time (see MeetingTypesPage precedent).
      operator: 'equals',
      value: pipelineId,
      association: pipelineAssociationTypeName,
    },
  ];
}
